import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { computed, inject, Signal } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  AccessLevel,
  DocumentAttribute,
  DocumentReferenceEntityType,
  DocumentTokenAction,
  DocumentType,
  DocumentUploadStatus,
  ItemDocumentReference,
  VirusScanStatus,
} from '@qtek/shared/models';
import { buildDocumentReferenceFormData } from '@qtek/shared/utils';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  interval,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  pipe,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs';
import {
  DocumentScanGate,
  ScanOutcome,
} from '../../document-scan-gate.service';
import { DocumentServingService } from '../../document-serving.service';
import { UploadSpeedMeter } from '../../upload-speed';
import { withDocumentReferenceMethods } from '../core/document-reference.store';
import {
  applyReferenceRemoval,
  applyReferenceUpsert,
  stampEntityType,
} from '../core/document-reference.updaters';
import { ItemDocumentReferenceService } from './item-document-reference.service';

export interface UploadItemImagePayload {
  file: Blob;
  fileName: string;
  entityId: string;
  description?: string;
  accessLevel?: AccessLevel;
}

/**
 * Tracks a two-stage (`generateToken` → `uploadWithToken`) item-document
 * upload. Keyed by the underlying **document** id — `referenceId` fills in
 * once the upload completes and the response carries the reference row's
 * own `_id` directly (see `uploadItemDocument`'s `Response` branch);
 * unset before that.
 */
export interface ItemUploadState {
  documentId: string;
  /** Owning deal id — this store is root-provided/shared across every open item tab, so callers must filter by it. */
  entityId: string;
  referenceId?: string;
  status: DocumentUploadStatus;
  progress?: number;
  speed?: number;
  /**
   * `loaded` has reached `total` but the server hasn't responded yet — XHR's
   * `loaded` only counts bytes handed to the socket send buffer, not bytes
   * the server acknowledged, so there's a real gap here for a large upload.
   * `speed` is frozen (not recomputed) while this is true; the UI should
   * show a "finalizing" state instead of the last (buffer-drain) rate.
   */
  settling?: boolean;
  /** Matches the legacy `PreUploadState`/data-table column naming (`filename`/`filesize`), not camelCase. */
  filename: string;
  filesize: number;
  categoryId?: string;
  extractionPromptSettled?: boolean;
  /** HTTP status of a `FAILED` upload's error response, when there was one. */
  errorStatus?: number;
  /** Translation key of a `FAILED` upload's reason — the backend's `sts` message key when it sent one (e.g. `ERR_FILE_UPLOAD_ERR` on a retryable 503). */
  errorKey?: string;
}

export interface UploadItemDocumentPayload {
  file: File;
  entityId: string;
  categoryId?: string;
  accessLevel?: AccessLevel;
  description?: string;
}

/**
 * Every editable field on an ItemDocumentReference through one PUT —
 * `categoryId` alone (move), `document.documentType` alone (classify),
 * `description` alone (rename), or any combination in a single request
 * (e.g. classify-and-file a freshly uploaded term sheet).
 */
export interface ItemDocumentUpdatePayload {
  attribute?: DocumentAttribute;
  description?: string;
  /** `null` = move to the data-room root. Omit entirely to leave the folder untouched. */
  categoryId?: string | null;
  document?: { documentType?: DocumentType };
}

interface ItemDocumentReferenceStoreState {
  uploads: Record<string, ItemUploadState>;
}

// XHR emits no progress while an upload is stalled, so the speed is re-sampled on a timer.
const STALL_TICK_MS = 1000;
const STALL_TICK = 'stallTick' as const;

/**
 * Generic document-reference state/calls plus item-main-image convenience,
 * a document-tab `ITEM_ATTACHMENT` list, live WS sync for that list, and the
 * two-stage upload flow the new backend doesn't have single-step tooling
 * for yet (see the data-room migration plan).
 *
 * `itemImages`/`mainImage` are scoped by `itemId` through `referencesFor`/
 * `latestFor`, so multiple items' images coexist in state instead of each
 * consuming component re-filtering a shared array.
 */
export const ItemDocumentReferenceStore = signalStore(
  { providedIn: 'root' },
  withDocumentReferenceMethods(ItemDocumentReferenceService),
  withState<ItemDocumentReferenceStoreState>({ uploads: {} }),
  withMethods(
    (
      store,
      service = inject(ItemDocumentReferenceService),
      documentServing = inject(DocumentServingService),
      scanGate = inject(DocumentScanGate)
    ) => {
      const disconnect$ = new Subject<string>();
      /** documentIds canceled via `cancelUpload` — one shared stream, not a root-scoped effect per upload that never gets destroyed. */
      const canceled$ = new Subject<string>();
      const connectedEntities = new Set<string>();

      /**
       * Per-entityId memo for `uploadsForItem`, mirroring
       * `referencesFor`'s cache in the core store (`document-reference.store.ts`).
       * Without it, every caller (the upload-status popover's `allFiles` and
       * `DataRoomDocumentsListComponent#watchUploadsForClassification`) built
       * its own independent, uncached `computed()` over the same entityId —
       * each re-running its own `Object.values(uploads()).filter(...)` pass
       * on every single upload-state change. A bulk upload of hundreds of
       * files made that pass, and the change-detection it triggers, the
       * dominant per-tick cost (SD-2716 bulk-upload-slowdown investigation).
       */
      const uploadsForItemCache = new Map<
        string,
        Signal<readonly ItemUploadState[]>
      >();

      /** Identity comparator for `uploadsForItem` — see `sameReferences` in the core store. */
      const sameUploads = (
        a: readonly ItemUploadState[],
        b: readonly ItemUploadState[]
      ): boolean =>
        a === b ||
        (a.length === b.length &&
          a.every((upload, index) => upload === b[index]));

      const patchUpload = (
        documentId: string,
        partial: Partial<ItemUploadState>
      ): void => {
        patchState(store, state => ({
          uploads: {
            ...state.uploads,
            [documentId]: { ...state.uploads[documentId], ...partial },
          },
        }));
      };

      /**
       * Swaps a queued row's client-side `pendingId` key for the real
       * `documentId` once `generateToken` responds, keeping its position so
       * the popover rows don't reorder and the batch total doesn't blip.
       */
      const rekeyUpload = (pendingId: string, documentId: string): void => {
        patchState(store, state => ({
          uploads: Object.fromEntries(
            Object.entries(state.uploads).map(([key, upload]) =>
              key === pendingId
                ? [documentId, { ...upload, documentId }]
                : [key, upload]
            )
          ),
        }));
      };

      /** Status and main `sts` message key of a failed upload request, same lookup as the global error interceptor. */
      const uploadErrorOf = (
        error: unknown
      ): Pick<ItemUploadState, 'errorStatus' | 'errorKey'> => {
        if (!(error instanceof HttpErrorResponse)) {
          return {};
        }
        const attrs: Record<string, { main?: boolean; msg?: string }> =
          error.error?.sts?.attrs ?? {};
        const [key, attr] =
          Object.entries(attrs).find(([, value]) => value?.main) ??
          Object.entries(attrs)[0] ??
          [];
        return {
          errorStatus: error.status || undefined,
          errorKey: attr?.msg ?? key,
        };
      };

      /**
       * Every ItemDocumentReference PUT this store issues must carry
       * `categoryId` — an omitted field is treated as `null` server-side,
       * bouncing the document to the data-room root. An explicit
       * `categoryId` in `payload` (including explicitly `null`, i.e. move
       * to root) always wins; otherwise falls back to the document's
       * current cached `categoryId`, so callers that aren't touching the
       * folder don't have to know or care about it. Single implementation
       * behind `updateDocument` — also called directly (not through
       * `store.updateDocument`) by the post-upload description write below,
       * since that runs inside this same `withMethods` factory before
       * `store.updateDocument` exists.
       */
      const putItemDocument = (
        id: string,
        payload: ItemDocumentUpdatePayload
      ): Observable<ItemDocumentReference | null> => {
        const categoryId =
          'categoryId' in payload
            ? payload.categoryId
            : store.referenceById(id)()?.categoryId;

        return service
          .put(
            { ...payload, ...(categoryId !== undefined ? { categoryId } : {}) },
            id
          )
          .pipe(
            map(({ res }): ItemDocumentReference | null =>
              res
                ? { ...res, entityType: DocumentReferenceEntityType.ITEM }
                : null
            ),
            tap(reference => {
              if (reference) {
                patchState(store, state =>
                  applyReferenceUpsert(state, reference)
                );
              }
            })
          );
      };

      return {
        itemImages(itemId: string): Signal<readonly ItemDocumentReference[]> {
          return store.referencesFor({
            attribute: DocumentAttribute.ITEM_MAIN_IMAGE,
            entityId: itemId,
          });
        },

        mainImage(itemId: string): Signal<ItemDocumentReference | null> {
          return store.latestFor({
            attribute: DocumentAttribute.ITEM_MAIN_IMAGE,
            entityId: itemId,
          });
        },

        loadItemImages(entityId: string): void {
          store.load({
            attribute: DocumentAttribute.ITEM_MAIN_IMAGE,
            entityId,
          });
        },

        uploadItemImage(
          payload: UploadItemImagePayload
        ): Observable<ItemDocumentReference | null> {
          const formData = buildDocumentReferenceFormData({
            file: payload.file,
            fileName: payload.fileName,
            entityType: DocumentReferenceEntityType.ITEM,
            entityId: payload.entityId,
            attribute: DocumentAttribute.ITEM_MAIN_IMAGE,
            accessLevel: payload.accessLevel ?? AccessLevel.PUBLIC,
            description: payload.description ?? 'Item Image',
          });

          return store.upload(formData);
        },

        removeItemImage(id: string): Observable<void> {
          return store.remove(id);
        },

        /** The document tab's file list — `ITEM_ATTACHMENT`-attributed references for one deal. */
        itemDocuments(
          itemId: string
        ): Signal<readonly ItemDocumentReference[]> {
          return store.referencesFor({
            attribute: DocumentAttribute.ITEM_ATTACHMENT,
            entityId: itemId,
          });
        },

        loadItemDocuments(
          entityId: string,
          options?: { force?: boolean }
        ): void {
          store.load({
            attribute: DocumentAttribute.ITEM_ATTACHMENT,
            entityId,
            force: options?.force,
          });
        },

        /** Drops this deal's document-tab list from the shared store — see `DataRoomComponent.ngOnDestroy`. */
        evictItemDocuments(entityId: string): void {
          store.evict({
            attribute: DocumentAttribute.ITEM_ATTACHMENT,
            entityId,
          });
        },

        /**
         * Live WS sync for one item's document list — mirrors the now-removed
         * v1 `DocumentsStore.connect`, but upserts into the generic
         * `referenceMap` instead of a flat array. **Unverified against a
         * live socket** — confirm the WS entity name equals
         * `'ItemDocumentReference'` and that an `ins` fires when
         * `generateToken` provisions a reference row.
         *
         * Idempotent and keyed per `entityId` — a second `connect` for an
         * already-connected item no-ops, and `disconnect(entityId)` only
         * tears down that item's own subscription. This store is
         * root-provided/shared across every open item tab (see
         * `ItemUploadState.entityId`'s docblock), so an unkeyed teardown
         * would kill every other open tab's live feed — same pattern as
         * `FolderStore.connect`/`disconnect`.
         */
        connect(entityId: string): void {
          if (connectedEntities.has(entityId)) return;
          connectedEntities.add(entityId);

          service
            .subscribe({
              mysid: `item-doc-refs-${entityId}`,
              prms: {
                entityId,
                attribute: DocumentAttribute.ITEM_ATTACHMENT,
                auditNames: 'true',
              },
            })
            .pipe(
              takeUntil(disconnect$.pipe(filter(id => id === entityId))),
              catchError(() => of(null)),
              finalize(() => connectedEntities.delete(entityId))
            )
            .subscribe((message: any) => {
              if (!message) return;
              const { op, res, id } = message;
              const rows: ItemDocumentReference[] = Array.isArray(res)
                ? res
                : res
                  ? [res]
                  : [];
              const stamped = stampEntityType(
                rows,
                DocumentReferenceEntityType.ITEM
              );

              switch (op) {
                case 'query':
                case 'ins':
                case 'upd':
                  stamped.forEach(reference => {
                    patchState(store, state =>
                      applyReferenceUpsert(state, reference)
                    );
                  });
                  break;
                case 'del': {
                  // A delete broadcast rebuilds a scope-only skeleton server-side (the row is
                  // already gone), which never carries its own `_id` — the real deleted id is
                  // only on the message's top-level `id` (see FolderStore.connect's `del` case).
                  const deletedId = res?._id ?? id;
                  if (!deletedId) break;
                  patchState(store, state =>
                    applyReferenceRemoval(state, deletedId)
                  );
                  break;
                }
                default:
                  break;
              }
            });
        },

        disconnect(entityId: string): void {
          disconnect$.next(entityId);
        },

        /**
         * Uploads tracked for one deal — this store is shared, so scope by
         * `entityId` rather than reading `uploads()` raw. Memoized per
         * `entityId` (see `uploadsForItemCache` above) so repeated callers
         * (popover + document list) share one computed and one filtered
         * array when nothing for this entity actually changed.
         */
        uploadsForItem(entityId: string): Signal<readonly ItemUploadState[]> {
          let cached = uploadsForItemCache.get(entityId);

          if (!cached) {
            cached = computed(
              () =>
                Object.values(store.uploads()).filter(
                  upload => upload.entityId === entityId
                ),
              { equal: sameUploads }
            );
            uploadsForItemCache.set(entityId, cached);
          }

          return cached;
        },

        /**
         * Fetches a reference's access audit trail (`documentAccessList`) via
         * `GET .../{id}?view=access` — the v2 replacement for the v1 `docacs`
         * query (`ItemDocumentReferenceEntityTask#accessView`). Deliberately
         * **not** routed through `store.fetchById`/`commitSingle`: those
         * upsert the response wholesale into `referenceMap`, and the access
         * view's backend projection is a thin `{id, documentAccessList}` (see
         * `accessView()` in the entity task) — upserting it would blank out
         * the fuller row the data-room list already has cached for this
         * document. Read-only, dialog-local; never commits to state.
         */
        fetchAccessView(id: string): Observable<ItemDocumentReference | null> {
          return service
            .get(id, { view: 'access' })
            .pipe(
              map(({ res }): ItemDocumentReference | null =>
                res
                  ? { ...res, entityType: DocumentReferenceEntityType.ITEM }
                  : null
              )
            );
        },

        /** Flips an in-flight upload to canceled — `uploadItemDocument`'s pipeline unwinds via `takeUntil`. */
        cancelUpload(documentId: string): void {
          patchUpload(documentId, { status: DocumentUploadStatus.CANCELED });
          canceled$.next(documentId);
        },

        /**
         * Lists a file that was never handed to `uploadItemDocument` as
         * `FAILED` (e.g. its dropped folder couldn't be created), so it shows
         * up in the upload status instead of silently going elsewhere.
         */
        registerFailedUpload(
          payload: Pick<
            ItemUploadState,
            'entityId' | 'filename' | 'filesize' | 'errorKey'
          >
        ): void {
          const documentId = `pending-${crypto.randomUUID()}`;
          patchUpload(documentId, {
            ...payload,
            documentId,
            status: DocumentUploadStatus.FAILED,
          });
        },

        settleExtractionPrompt(documentId: string): void {
          if (!store.uploads()[documentId]) {
            return;
          }
          patchUpload(documentId, { extractionPromptSettled: true });
        },

        /** Drops tracked upload entries once the caller (e.g. the upload-status popover) is done with them. */
        clearUploads(documentIds: readonly string[]): void {
          patchState(store, state => {
            const uploads = { ...state.uploads };
            documentIds.forEach(id => delete uploads[id]);
            return { uploads };
          });
        },

        /** Mixed-selection bulk delete's document half — see `FolderStore.bulkDelete` for folders. */
        bulkDeleteDocuments(ids: readonly string[]): Observable<void> {
          return service.bulkDelete(ids).pipe(
            tap(() => {
              ids.forEach(id => {
                patchState(store, state => applyReferenceRemoval(state, id));
              });
            }),
            map((): void => undefined)
          );
        },

        /**
         * Single write path for every ItemDocumentReference field update —
         * `categoryId` alone (move), `document.documentType` alone
         * (classify), `description` alone (rename), or any combination in
         * one PUT (e.g. classify-and-file a freshly uploaded document).
         * `document.documentType` write is **unconfirmed against a live
         * backend**: there is no documented endpoint for writing this
         * nested field; this assumes `PUT /api/v2/model/ItemDocumentReference/{id}`
         * accepts a partial `document` sub-object the same way
         * `attribute`/`description` partial-update the reference row
         * itself. Ask backend to confirm or provide the real endpoint
         * before relying on this. `categoryId` is always sent defensively
         * regardless (via `putItemDocument`) — see its docblock.
         */
        updateDocument(
          id: string,
          payload: ItemDocumentUpdatePayload
        ): Observable<ItemDocumentReference | null> {
          return putItemDocument(id, payload);
        },

        /**
         * Waits out a freshly uploaded document's async virus scan. Extraction (and
         * anything else touching the bytes) must not run until this resolves `'ready'` —
         * see `DocumentScanGate.whenReferenceSettled` for the WS mechanics.
         */
        whenDocumentScanSettled(
          referenceId: string,
          entityId: string,
          virusScanStatus?: VirusScanStatus | null,
          timeoutMs?: number
        ): Observable<ScanOutcome> {
          return scanGate.whenReferenceSettled(service, {
            referenceId,
            attribute: DocumentAttribute.ITEM_ATTACHMENT,
            entityId,
            virusScanStatus,
            timeoutMs,
          });
        },

        /**
         * Two-stage upload — the new backend has no single-request
         * equivalent that also pre-provisions a placeholder row.
         * `generateToken` (action UPLOAD) provisions the document, then
         * `uploadWithToken` streams the bytes with progress and returns the
         * finished reference row directly. Concurrency capped at 5, same as
         * the legacy `preUploadAndTrack`.
         */
        uploadItemDocument: rxMethod<UploadItemDocumentPayload>(
          pipe(
            // Register every file as soon as it's submitted — before the
            // concurrency cap below — so the "n/m" badge shows the whole
            // batch total up front instead of growing 5 files at a time.
            // No documentId exists until `generateToken` responds, so the
            // row is keyed on a client-generated id until then.
            map(payload => {
              const pendingId = `pending-${crypto.randomUUID()}`;
              patchUpload(pendingId, {
                documentId: pendingId,
                entityId: payload.entityId,
                status: DocumentUploadStatus.CREATED,
                filename: payload.file.name,
                filesize: payload.file.size,
                categoryId: payload.categoryId,
              });
              return { ...payload, pendingId };
            }),
            mergeMap(
              ({
                file,
                entityId,
                categoryId,
                accessLevel,
                description,
                pendingId,
              }) => {
                // Canceled from the popover while still queued — never
                // provision it.
                if (
                  store.uploads()[pendingId]?.status ===
                  DocumentUploadStatus.CANCELED
                ) {
                  return EMPTY;
                }

                return documentServing
                  .generateToken(
                    {
                      action: DocumentTokenAction.UPLOAD,
                      entityType: DocumentReferenceEntityType.ITEM,
                      entityId,
                      documentName: file.name,
                      contentType: file.type,
                      categoryId,
                      accessLevel: accessLevel ?? AccessLevel.SHARED,
                    },
                    { skipErrorHandling: true }
                  )
                  .pipe(
                    catchError(error => {
                      patchUpload(pendingId, {
                        status: DocumentUploadStatus.FAILED,
                        ...uploadErrorOf(error),
                      });
                      return EMPTY;
                    }),
                    tap(({ res }) => {
                      if (!res) return;
                      // Keeps the queued row's status — a cancel that landed
                      // while the token was in flight stays CANCELED, and the
                      // byte upload below is then never started.
                      rekeyUpload(pendingId, res.documentIds[0]);
                    }),
                    switchMap(({ res }) => {
                      if (!res) return EMPTY;
                      const { token, documentIds } = res;
                      const documentId = documentIds[0];

                      if (
                        store.uploads()[documentId]?.status ===
                        DocumentUploadStatus.CANCELED
                      ) {
                        return EMPTY;
                      }

                      // Cancellation signal: `cancelUpload` for this upload
                      // (called by e.g. the upload-status popover).
                      const canceledThis$ = canceled$.pipe(
                        filter(id => id === documentId)
                      );

                      // One meter per upload, seeded on `Sent` so connection
                      // setup isn't counted as transfer time.
                      const speedMeter = new UploadSpeedMeter();
                      let lastLoaded = 0;
                      let lastProgressAt: number | undefined;

                      return merge(
                        documentServing.uploadWithToken(
                          file,
                          token,
                          documentId,
                          file.name,
                          { skipErrorHandling: true }
                        ),
                        interval(STALL_TICK_MS).pipe(map(() => STALL_TICK))
                      ).pipe(
                        takeUntil(canceledThis$),
                        takeWhile(
                          event =>
                            event === STALL_TICK ||
                            event.type !== HttpEventType.Response,
                          true
                        ),
                        filter(
                          event =>
                            event === STALL_TICK ||
                            event.type === HttpEventType.Response ||
                            event.type === HttpEventType.UploadProgress ||
                            event.type === HttpEventType.Sent
                        ),
                        tap(event => {
                          if (event === STALL_TICK) {
                            const now = performance.now();
                            const current = store.uploads()[documentId];
                            if (
                              lastProgressAt === undefined ||
                              current?.settling ||
                              now - lastProgressAt < STALL_TICK_MS
                            ) {
                              return;
                            }
                            const speed = speedMeter.sample(lastLoaded, now);
                            if (
                              speed !== undefined &&
                              speed !== current?.speed
                            ) {
                              patchUpload(documentId, { speed });
                            }
                            return;
                          }
                          switch (event.type) {
                            case HttpEventType.Sent:
                              lastProgressAt = performance.now();
                              speedMeter.reset(lastProgressAt);
                              patchUpload(documentId, {
                                status: DocumentUploadStatus.STARTED,
                              });
                              break;
                            case HttpEventType.UploadProgress: {
                              // `event.total` (multipart framing incl.
                              // boundaries/headers) can exceed `file.size`,
                              // so cap the displayed percentage at 100.
                              const total = event.total ?? file.size;
                              const progress = Math.min(
                                100,
                                Math.round((100 * event.loaded) / total)
                              );

                              const current = store.uploads()[documentId];

                              if (event.loaded >= total) {
                                // Bytes are handed to the socket buffer,
                                // not yet acknowledged by the server —
                                // freeze speed and show "finalizing"
                                // rather than publish the buffer-drain
                                // rate as the upload rate.
                                if (
                                  current?.progress !== 100 ||
                                  !current?.settling
                                ) {
                                  patchUpload(documentId, {
                                    progress: 100,
                                    settling: true,
                                  });
                                }
                                break;
                              }

                              lastLoaded = event.loaded;
                              lastProgressAt = performance.now();
                              const speed = speedMeter.sample(
                                event.loaded,
                                lastProgressAt
                              );

                              // A raw XHR `UploadProgress` event can fire far more often
                              // than the rounded percentage actually moves (SD-2716); a
                              // no-op patch here still bumps `uploads`' identity and
                              // forces every `uploadsForItem`/`references` consumer to
                              // re-derive and every subscribed component to re-render.
                              if (
                                current?.progress !== progress ||
                                speed !== undefined
                              ) {
                                patchUpload(documentId, {
                                  progress,
                                  ...(speed !== undefined ? { speed } : {}),
                                });
                              }
                              break;
                            }
                            case HttpEventType.Response: {
                              const uploaded = (
                                event.body as
                                  | { res?: ItemDocumentReference }
                                  | undefined
                              )?.res;

                              if (uploaded) {
                                // The upload response is the abstract
                                // reference shape and omits item-only
                                // fields (categoryId, workflowId, ...) —
                                // merge over whatever's cached rather than
                                // upserting wholesale, or a freshly
                                // uploaded doc would appear to bounce out
                                // of its folder.
                                const cached = store.referenceById(
                                  uploaded._id
                                )();
                                const merged: ItemDocumentReference = {
                                  ...cached,
                                  ...uploaded,
                                  entityType: DocumentReferenceEntityType.ITEM,
                                  categoryId:
                                    uploaded.categoryId ??
                                    cached?.categoryId ??
                                    categoryId,
                                };
                                patchState(store, state =>
                                  applyReferenceUpsert(state, merged)
                                );
                                patchUpload(documentId, {
                                  status: DocumentUploadStatus.COMPLETED,
                                  referenceId: merged._id,
                                  settling: false,
                                });
                                if (description) {
                                  putItemDocument(merged._id, {
                                    description,
                                  }).subscribe();
                                }
                              } else {
                                patchUpload(documentId, {
                                  status: DocumentUploadStatus.COMPLETED,
                                  settling: false,
                                });
                              }
                              break;
                            }
                            default:
                              break;
                          }
                        }),
                        catchError(error => {
                          const upload = store.uploads()[documentId];
                          // A user-initiated cancel already set CANCELED
                          // (see `cancelUpload`) before `takeUntil(canceledThis$)`
                          // tears the request down — don't relabel that as
                          // a failure just because the teardown itself
                          // surfaces as an error on this stream.
                          if (
                            upload?.status !== DocumentUploadStatus.CANCELED
                          ) {
                            patchUpload(documentId, {
                              status: DocumentUploadStatus.FAILED,
                              ...uploadErrorOf(error),
                            });
                          }
                          if (upload?.referenceId) {
                            service.delete(upload.referenceId).subscribe();
                          }
                          return of(null);
                        })
                      );
                    })
                  );
              },
              5
            )
          )
        ),
      };
    }
  )
);
